/*
 * @providesModule ScreenFocusTracker
 *
 * Utility functions to track which screen is currently in focus,
 * considering tab navigation, drawer, and navigation stacks.
 */

import GlobalEventEmitter from 'GlobalEventEmitter';
import NavigationManager from 'NavigationManager';
import HomeScreenConstants from 'HomeScreenConstants';
import {navigationRef} from 'NavigationRef';
import type {EmitterSubscription} from 'react-native';

export type FocusInfo = {
  homeTab: string | null; // 'PLAY', 'HOME', 'STORE', etc.
  homeTabIndex: number | null; // 0, 1, 2, etc.
  topRouteName: string | null; // Top route in React Navigation stack
  screenIdentifier: string; // Combined identifier like "PLAY:HomeScreen"
  isPlayTabFocused: boolean; // Quick check for Play tab
  isDrawerOpen: boolean; // Whether side menu is open
  isHomeScreenActuallyVisible: boolean; // Whether home screen is truly visible (no screens stacked on top)
  legacyStackDepth: number; // Number of screens in the legacy navigator stack
  timestamp: number; // When this info was captured
};

export type FocusChangeListener = (focusInfo: FocusInfo) => void;

class ScreenFocusTracker {
  private currentFocusInfo: FocusInfo | null = null;
  private listeners: Set<FocusChangeListener> = new Set();
  private subscriptions: EmitterSubscription[] = [];
  private initialized = false;
  private isDrawerOpen = false;
  private currentTabIndex: number | null = null;
  private legacyStackDepth: number = 1; // Start with 1 (home screen)
  private reactNavigationUnsubscribe: (() => void) | null = null; // React Navigation state listener

  /**
   * Initialize the focus tracker. This sets up listeners for navigation changes
   * and tab changes to track focus state.
   */
  initialize(): void {
    if (this.initialized) {
      console.warn('[ScreenFocusTracker] Already initialized');
      return;
    }

    console.log('[ScreenFocusTracker] Initializing screen focus tracking');

    // Listen for tab changes
    const tabSelectedSub = GlobalEventEmitter.addListener(
      'TabSelected' as any,
      this.handleTabChange
    );

    // Listen for home screen tab changes
    const homeTabChangedSub = GlobalEventEmitter.addListener(
      'HomeScreenTabChanged' as any,
      this.handleHomeScreenTabChange
    );

    // Listen for HomeScreen appearance (first launch)
    const homeScreenDidAppearSub = GlobalEventEmitter.addListener(
      'HomeScreenDidAppear' as any,
      this.handleHomeScreenDidAppear
    );

    // Listen for drawer state changes
    const drawerOpenedSub = GlobalEventEmitter.addListener(
      'SideMenuDidOpen' as any,
      this.handleDrawerOpened
    );

    const drawerClosedSub = GlobalEventEmitter.addListener(
      'SideMenuDidClose' as any,
      this.handleDrawerClosed
    );

    // Listen for screen push/pop events to track navigation stack depth
    const screenWillBlurSub = GlobalEventEmitter.addListener(
      'ScreenWillBlur' as any,
      this.handleScreenWillBlur
    );

    const screenDidFocusSub = GlobalEventEmitter.addListener(
      'ScreenDidFocus' as any,
      this.handleScreenDidFocus
    );

    // Listen for legacy navigator route changes
    const legacyScreenPushedSub = GlobalEventEmitter.addListener(
      'LegacyScreenPushed' as any,
      this.handleLegacyScreenPushed
    );

    const legacyScreenPoppedSub = GlobalEventEmitter.addListener(
      'LegacyScreenPopped' as any,
      this.handleLegacyScreenPopped
    );

    this.subscriptions = [
      tabSelectedSub,
      homeTabChangedSub,
      homeScreenDidAppearSub,
      drawerOpenedSub,
      drawerClosedSub,
      screenWillBlurSub,
      screenDidFocusSub,
      legacyScreenPushedSub,
      legacyScreenPoppedSub,
    ];

    // 🎯 Listen for React Navigation state changes (for SkillzRouter.push navigations)
    // This handles cases where screens are pushed via React Navigation instead of legacy navigator
    if (navigationRef?.current) {
      this.reactNavigationUnsubscribe = navigationRef.current.addListener('state', this.handleReactNavigationStateChange);
    }

    this.initialized = true;

    // Get initial state (including legacy stack depth)
    this.refreshLegacyStackDepth();
    this.updateFocusInfo();
  }

  /**
   * Clean up listeners when no longer needed
   */
  cleanup(): void {
    console.log('[ScreenFocusTracker] Cleaning up');
    this.subscriptions.forEach((sub) => sub.remove());
    this.subscriptions = [];
    this.listeners.clear();
    this.initialized = false;
    this.currentFocusInfo = null;
    
    // Clean up React Navigation listener
    if (this.reactNavigationUnsubscribe) {
      this.reactNavigationUnsubscribe();
      this.reactNavigationUnsubscribe = null;
    }
  }

  /**
   * Get the current focus information
   * @returns Current focus info including which tab and screen is focused
   */
  getCurrentFocusInfo(): FocusInfo {
    if (!this.currentFocusInfo) {
      this.updateFocusInfo();
    }
    return this.currentFocusInfo!;
  }

  /**
   * Quick check if the Play tab is currently in focus
   * @returns true if Play tab is focused, drawer is closed, AND no screens stacked on top
   */
  isPlayTabInFocus(): boolean {
    const focusInfo = this.getCurrentFocusInfo();
    // Use isHomeScreenActuallyVisible which checks all conditions:
    // - Play tab focused
    // - Drawer closed
    // - No screens stacked on top (legacyStackDepth <= 1)
    return focusInfo.isHomeScreenActuallyVisible;
  }

  /**
   * Add a listener to be notified when focus changes
   * @param listener Function to call when focus changes
   * @returns Function to remove the listener
   */
  addFocusChangeListener(listener: FocusChangeListener): () => void {
    this.listeners.add(listener);
    
    // Immediately call with current state
    if (this.currentFocusInfo) {
      listener(this.currentFocusInfo);
    }

    return () => {
      this.listeners.delete(listener);
    };
  }

  /**
   * Refresh the legacy stack depth from the navigator
   */
  private refreshLegacyStackDepth = (): void => {
    try {
      const navigator = NavigationManager.getNavigator();
      if (navigator) {
        const routes = navigator.getCurrentRoutes?.() || [];
        this.legacyStackDepth = routes.length;
      }
    } catch (error) {
      console.warn('[ScreenFocusTracker] Error refreshing legacy stack depth:', error);
    }
  };

  /**
   * Update the current focus information and notify listeners
   */
  private updateFocusInfo = (): void => {
    try {
      const homeTab = NavigationManager.getCurrentHomeTab();
      const topRouteName = navigationRef?.current?.getCurrentRoute?.()?.name || null;
      const screenIdentifier = NavigationManager.getCurrentScreenIdentifier();
      const homeTabIndex = this.getTabIndexFromName(homeTab);
      
      // Check if Play tab is focused
      const isPlayTabFocused = 
        homeTabIndex === HomeScreenConstants.PLAY_INDEX || 
        homeTab === HomeScreenConstants.PLAY;
      
      // Get React Navigation stack depth
      const reactNavState = navigationRef?.current?.getState?.();
      const reactNavRoutes = reactNavState?.routes || [];
      const reactNavStackDepth = reactNavRoutes.length;
      
      // Check if React Navigation is on the entry/home screen
      // Entry screen is the root, so stack depth of 1 with ENTRY_SCREEN route means home is visible
      const isOnReactNavHomeScreen = reactNavStackDepth <= 1 || 
        (topRouteName === 'ENTRY_SCREEN' || topRouteName === 'HomeScreen' || topRouteName === null);
      
      // Home screen is only actually visible if:
      // 1. Play tab is focused
      // 2. Drawer is closed
      // 3. No legacy screens are stacked on top (legacyStackDepth <= 1)
      // 4. No React Navigation screens are stacked on top (or we're on the home screen route)
      const isHomeScreenActuallyVisible = 
        isPlayTabFocused && 
        !this.isDrawerOpen && 
        this.legacyStackDepth <= 1 &&
        isOnReactNavHomeScreen;

      const newFocusInfo: FocusInfo = {
        homeTab,
        homeTabIndex,
        topRouteName,
        screenIdentifier,
        isPlayTabFocused,
        isDrawerOpen: this.isDrawerOpen,
        isHomeScreenActuallyVisible,
        legacyStackDepth: this.legacyStackDepth,
        timestamp: Date.now(),
      };

      // Check if focus actually changed
      const focusChanged = this.hasFocusChanged(newFocusInfo);

      this.currentFocusInfo = newFocusInfo;

      if (focusChanged) {
        console.log('[ScreenFocusTracker] Focus changed:', {
          homeTab: newFocusInfo.homeTab,
          homeTabIndex: newFocusInfo.homeTabIndex,
          topRouteName: newFocusInfo.topRouteName,
          isPlayTabFocused: newFocusInfo.isPlayTabFocused,
          isDrawerOpen: newFocusInfo.isDrawerOpen,
          isHomeScreenActuallyVisible: newFocusInfo.isHomeScreenActuallyVisible,
          legacyStackDepth: newFocusInfo.legacyStackDepth,
          reactNavStackDepth,
          isOnReactNavHomeScreen,
        });

        this.notifyListeners(newFocusInfo);
      }
    } catch (error) {
      console.error('[ScreenFocusTracker] Error updating focus info:', error);
    }
  };

  /**
   * Check if focus has meaningfully changed
   */
  private hasFocusChanged(newInfo: FocusInfo): boolean {
    if (!this.currentFocusInfo) {
      return true;
    }

    return (
      this.currentFocusInfo.homeTab !== newInfo.homeTab ||
      this.currentFocusInfo.homeTabIndex !== newInfo.homeTabIndex ||
      this.currentFocusInfo.topRouteName !== newInfo.topRouteName ||
      this.currentFocusInfo.isDrawerOpen !== newInfo.isDrawerOpen ||
      this.currentFocusInfo.isHomeScreenActuallyVisible !== newInfo.isHomeScreenActuallyVisible ||
      this.currentFocusInfo.legacyStackDepth !== newInfo.legacyStackDepth
    );
  }

  /**
   * Convert tab name to index
   */
  private getTabIndexFromName(tabName: string | null): number | null {
    if (!tabName) {
      return this.currentTabIndex;
    }

    switch (tabName) {
      case HomeScreenConstants.STREAM:
      case 'HOME':
        return HomeScreenConstants.STREAM_INDEX;
      case HomeScreenConstants.PLAY:
        return HomeScreenConstants.PLAY_INDEX;
      case HomeScreenConstants.STORE:
        return HomeScreenConstants.STORE_INDEX;
      case HomeScreenConstants.LEAGUES:
        return HomeScreenConstants.LEAGUES_INDEX;
      case HomeScreenConstants.MENU:
        return HomeScreenConstants.MENU_INDEX;
      default:
        return this.currentTabIndex;
    }
  }

  /**
   * Convert tab index to name
   */
  private getTabNameFromIndex(tabIndex: number): string {
    switch (tabIndex) {
      case HomeScreenConstants.STREAM_INDEX:
        return HomeScreenConstants.STREAM;
      case HomeScreenConstants.PLAY_INDEX:
        return HomeScreenConstants.PLAY;
      case HomeScreenConstants.STORE_INDEX:
        return HomeScreenConstants.STORE;
      case HomeScreenConstants.LEAGUES_INDEX:
        return HomeScreenConstants.LEAGUES;
      case HomeScreenConstants.MENU_INDEX:
        return HomeScreenConstants.MENU;
      default:
        return 'UNKNOWN';
    }
  }

  /**
   * Notify all listeners of focus change
   */
  private notifyListeners(focusInfo: FocusInfo): void {
    this.listeners.forEach((listener) => {
      try {
        listener(focusInfo);
      } catch (error) {
        console.error('[ScreenFocusTracker] Error in listener:', error);
      }
    });
  }

  /**
   * Handle tab selection event
   */
  private handleTabChange = (tabIndex: number): void => {
    console.log('[ScreenFocusTracker] Tab changed to index:', tabIndex);
    this.currentTabIndex = tabIndex;
    
    const tabName = this.getTabNameFromIndex(tabIndex);
    NavigationManager.setCurretHomeTab(tabName);
    
    this.updateFocusInfo();
  };

  /**
   * Handle home screen tab change event with more details
   */
  private handleHomeScreenTabChange = (event: {
    previousTab: number;
    currentTab: number;
    previousTabName: string;
    currentTabName: string;
  }): void => {
    console.log('[ScreenFocusTracker] HomeScreen tab changed:', event);
    this.currentTabIndex = event.currentTab;
    this.updateFocusInfo();
  };

  /**
   * Handle HomeScreen appearance (important for first launch)
   */
  private handleHomeScreenDidAppear = (): void => {
    console.log('[ScreenFocusTracker] HomeScreen appeared');
    
    // On first launch, set to initial tab if not already set
    if (this.currentTabIndex === null) {
      this.currentTabIndex = HomeScreenConstants.getHomescreenInitialTab();
      const tabName = this.getTabNameFromIndex(this.currentTabIndex);
      NavigationManager.setCurretHomeTab(tabName);
    }
    
    this.updateFocusInfo();
  };

  /**
   * Handle drawer opened
   */
  private handleDrawerOpened = (): void => {
    console.log('[ScreenFocusTracker] Drawer opened');
    this.isDrawerOpen = true;
    this.updateFocusInfo();
  };

  /**
   * Handle drawer closed
   */
  private handleDrawerClosed = (): void => {
    console.log('[ScreenFocusTracker] Drawer closed');
    this.isDrawerOpen = false;
    this.updateFocusInfo();
  };

  /**
   * Handle screen will blur (navigating away from a screen)
   */
  private handleScreenWillBlur = (screenName: string): void => {
    console.log('[ScreenFocusTracker] Screen will blur:', screenName);
    // Refresh stack depth when screen changes
    this.refreshLegacyStackDepth();
    this.updateFocusInfo();
  };

  /**
   * Handle screen did focus (arrived at a screen)
   */
  private handleScreenDidFocus = (screenName: string): void => {
    console.log('[ScreenFocusTracker] Screen did focus:', screenName);
    // Refresh stack depth when screen changes
    this.refreshLegacyStackDepth();
    this.updateFocusInfo();
  };

  /**
   * Handle legacy screen pushed onto stack
   */
  private handleLegacyScreenPushed = (event?: {scene?: string; resetStack?: boolean}): void => {
    if (event?.resetStack) {
      // Stack was reset with home + new route, so depth is 2
      this.legacyStackDepth = 2;
    } else {
      this.legacyStackDepth++;
    }
    console.log('[ScreenFocusTracker] Legacy screen pushed, new depth:', this.legacyStackDepth, 'scene:', event?.scene, 'resetStack:', event?.resetStack);
    this.updateFocusInfo();
  };

  /**
   * Handle legacy screen popped from stack
   */
  private handleLegacyScreenPopped = (event?: {scene?: string; poppedToRoot?: boolean}): void => {
    if (event?.poppedToRoot) {
      // If popped to root, reset to 1
      this.legacyStackDepth = 1;
    } else {
      this.legacyStackDepth = Math.max(1, this.legacyStackDepth - 1);
    }
    console.log('[ScreenFocusTracker] Legacy screen popped, new depth:', this.legacyStackDepth, 'poppedToRoot:', event?.poppedToRoot);
    this.updateFocusInfo();
  };

  /**
   * Handle React Navigation state changes
   * This catches navigations done via SkillzRouter.push() which don't go through legacy navigator
   */
  private handleReactNavigationStateChange = (): void => {
    // Get React Navigation stack depth
    const state = navigationRef?.current?.getState?.();
    const reactNavRoutes = state?.routes || [];
    const reactNavStackDepth = reactNavRoutes.length;
    
    // Get current route name
    const currentRoute = navigationRef?.current?.getCurrentRoute?.();
    const currentRouteName = currentRoute?.name || null;
    
    console.log('[ScreenFocusTracker] React Navigation state changed:', {
      reactNavStackDepth,
      currentRouteName,
      legacyStackDepth: this.legacyStackDepth,
    });
    
    // Refresh legacy stack depth as well (in case both navigators are used)
    this.refreshLegacyStackDepth();
    this.updateFocusInfo();
  };
}

// Export singleton instance
const screenFocusTracker = new ScreenFocusTracker();
export default screenFocusTracker;

/**
 * Convenience function to get current focus info
 * Usage: const focusInfo = getCurrentScreenFocus();
 */
export function getCurrentScreenFocus(): FocusInfo {
  return screenFocusTracker.getCurrentFocusInfo();
}

/**
 * Convenience function to check if Play tab is in focus
 * Usage: if (isPlayTabFocused()) { ... }
 */
export function isPlayTabFocused(): boolean {
  return screenFocusTracker.isPlayTabInFocus();
}
